/**
 * 🔐 ADMIN VERIFICATION REQUESTS LIST
 * Component for displaying and managing verification requests using new admin endpoints
 * Integrates with VerificationDocumentViewer for detailed image viewing
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Paper,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Stack,
  LinearProgress,
  Badge
} from '@mui/material';
import {
  VerifiedUser,
  Warning,
  Schedule,
  CheckCircle,
  Cancel,
  Visibility,
  Refresh,
  FilterList,
  Person,
  Description,
  Camera,
  Download,
  RemoveRedEye,
  Block
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { verificationService, AdminVerificationRequest } from '@/services/verificationService';
import VerificationDocumentViewer from './VerificationDocumentViewer';
import { EnhancedVerificationViewer } from './EnhancedVerificationViewer';

interface AdminVerificationRequestsListProps {
  onStatsUpdate?: (stats: any) => void;
}

interface FilterState {
  status: 'all' | 'pending_review' | 'pending_manual_review' | 'verified' | 'rejected';
  documentType: 'all' | 'passport' | 'national_id' | 'driver_license';
}

const AdminVerificationRequestsList: React.FC<AdminVerificationRequestsListProps> = ({
  onStatsUpdate
}) => {
  const [requests, setRequests] = useState<AdminVerificationRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<AdminVerificationRequest | null>(null);
  const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [actionNotes, setActionNotes] = useState('');
  const [useEnhancedViewer, setUseEnhancedViewer] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    type: 'success' as 'success' | 'error'
  });

  // Blacklist passport state
  const [blacklistDialogOpen, setBlacklistDialogOpen] = useState(false);
  const [blacklistPassportNumber, setBlacklistPassportNumber] = useState('');
  const [blacklistReason, setBlacklistReason] = useState('');
  const [blacklistRequest, setBlacklistRequest] = useState<AdminVerificationRequest | null>(null);

  const [filters, setFilters] = useState<FilterState>({
    status: 'all',
    documentType: 'all'
  });

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0
  });

  // Load verification requests
  const loadVerificationRequests = async () => {
    setIsLoading(true);
    try {
      const params = {
        status: filters.status !== 'all' ? filters.status : undefined
      };

      const result = await verificationService.getAdminVerificationRequests(params);
      
      if (result.success && result.data && result.data.data) {
        const requests = result.data.data;
        setRequests(requests);
        setPagination(prev => ({
          ...prev,
          total: result.data!.total
        }));
        
        // Update stats for parent component
        const stats = calculateStats(requests);
        onStatsUpdate?.(stats);
      } else {
        console.error('Failed to load verification requests:', result.error);
        
        // Still load mock data for development
        const mockRequests: AdminVerificationRequest[] = [
          {
            id: 'ver-001',
            verificationId: 'VER_1757945832073_s0b4an',
            userId: 'user-001',
            status: 'pending_review',
            documentType: 'passport',
            countryCode: 'NG',
            documentImageUrl: 'data:image/svg+xml;base64,' + btoa(`
              <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
                <rect width="400" height="300" fill="#2196F3"/>
                <text x="200" y="150" text-anchor="middle" fill="white" font-size="24">PASSPORT</text>
              </svg>
            `),
            selfieImageUrl: 'data:image/svg+xml;base64,' + btoa(`
              <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
                <rect width="400" height="300" fill="#4CAF50"/>
                <text x="200" y="150" text-anchor="middle" fill="white" font-size="24">SELFIE</text>
              </svg>
            `),
            userInfo: {
              firstName: 'John',
              lastName: 'Doe',
              email: 'john@example.com',
              phone: '+2348012345678'
            },
            uploadedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
          },
          {
            id: 'ver-002',
            verificationId: 'VER_1757945832074_t1c5bo',
            userId: 'user-002',
            status: 'pending_review',
            documentType: 'national_id',
            countryCode: 'NG',
            documentImageUrl: 'data:image/svg+xml;base64,' + btoa(`
              <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
                <rect width="400" height="300" fill="#FF9800"/>
                <text x="200" y="150" text-anchor="middle" fill="white" font-size="24">NATIONAL ID</text>
              </svg>
            `),
            selfieImageUrl: 'data:image/svg+xml;base64,' + btoa(`
              <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
                <rect width="400" height="300" fill="#9C27B0"/>
                <text x="200" y="150" text-anchor="middle" fill="white" font-size="24">SELFIE</text>
              </svg>
            `),
            userInfo: {
              firstName: 'Jane',
              lastName: 'Smith',
              email: 'jane@example.com',
              phone: '+2348087654321'
            },
            uploadedAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString()
          }
        ];
        
        setRequests(mockRequests);
        setPagination(prev => ({
          ...prev,
          total: mockRequests.length
        }));
        
        const stats = calculateStats(mockRequests);
        onStatsUpdate?.(stats);
      }
    } catch (error) {
      console.error('Error loading verification requests:', error);
      
      // Load mock data for development when API fails
      const mockRequests: AdminVerificationRequest[] = [
        {
          id: 'ver-001',
          verificationId: 'VER_1757945832073_s0b4an',
          userId: 'user-001',
          status: 'pending_review',
          documentType: 'passport',
          countryCode: 'NG',
          documentImageUrl: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
              <rect width="400" height="300" fill="#2196F3"/>
              <text x="200" y="150" text-anchor="middle" fill="white" font-size="24">PASSPORT</text>
            </svg>
          `),
          selfieImageUrl: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
              <rect width="400" height="300" fill="#4CAF50"/>
              <text x="200" y="150" text-anchor="middle" fill="white" font-size="24">SELFIE</text>
            </svg>
          `),
          userInfo: {
            firstName: 'John',
            lastName: 'Doe',
            email: 'john@example.com',
            phone: '+2348012345678'
          },
          uploadedAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
        },
        {
          id: 'ver-002',
          verificationId: 'VER_1757945832074_t1c5bo',
          userId: 'user-002',
          status: 'pending_review',
          documentType: 'national_id',
          countryCode: 'NG',
          documentImageUrl: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
              <rect width="400" height="300" fill="#FF9800"/>
              <text x="200" y="150" text-anchor="middle" fill="white" font-size="24">NATIONAL ID</text>
            </svg>
          `),
          selfieImageUrl: 'data:image/svg+xml;base64,' + btoa(`
            <svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
              <rect width="400" height="300" fill="#9C27B0"/>
              <text x="200" y="150" text-anchor="middle" fill="white" font-size="24">SELFIE</text>
            </svg>
          `),
          userInfo: {
            firstName: 'Jane',
            lastName: 'Smith',
            email: 'jane@example.com',
            phone: '+2348087654321'
          },
          uploadedAt: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString()
        }
      ];
      
      setRequests(mockRequests);
      setPagination(prev => ({
        ...prev,
        total: mockRequests.length
      }));
      
      const stats = calculateStats(mockRequests);
      onStatsUpdate?.(stats);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate statistics from requests
  const calculateStats = (requestList: AdminVerificationRequest[]) => {
    return {
      total: requestList.length,
      pending: requestList.filter(r => r.status === 'pending_review' || r.status === 'pending_manual_review').length,
      pendingManualReview: requestList.filter(r => r.status === 'pending_manual_review').length,
      verified: requestList.filter(r => r.status === 'verified').length,
      rejected: requestList.filter(r => r.status === 'rejected').length
    };
  };

  // Handle document viewer
  const handleViewDocuments = (request: AdminVerificationRequest) => {
    setSelectedRequest(request);
    setDocumentViewerOpen(true);
  };

  // Handle approve/reject actions
  const handleAction = (request: AdminVerificationRequest, action: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setActionType(action);
    setActionDialogOpen(true);
    setActionNotes('');
  };

  const handleActionConfirm = async () => {
    if (!selectedRequest || !actionType) return;

    setIsLoading(true);
    try {
      const result = await verificationService.updateVerificationRequestStatus(
        selectedRequest.verificationId,
        actionType,
        'admin-123', // Would get from auth context
        actionNotes || undefined
      );

      if (result.success) {
        setSnackbar({
          open: true,
          message: `Verification request ${actionType}d successfully`,
          type: 'success'
        });
        
        // Refresh the list
        await loadVerificationRequests();
      } else {
        setSnackbar({
          open: true,
          message: `Failed to ${actionType} verification request`,
          type: 'error'
        });
      }
    } catch (error) {
      console.error(`Error ${actionType}ing verification:`, error);
      setSnackbar({
        open: true,
        message: `Error ${actionType}ing verification request`,
        type: 'error'
      });
    } finally {
      setIsLoading(false);
      setActionDialogOpen(false);
      setSelectedRequest(null);
      setActionType(null);
    }
  };

  // Handle blacklist passport
  const handleBlacklistPassport = (request: AdminVerificationRequest) => {
    setBlacklistRequest(request);
    setBlacklistPassportNumber('');
    setBlacklistReason('');
    setBlacklistDialogOpen(true);
  };

  const handleBlacklistConfirm = async () => {
    if (!blacklistRequest || !blacklistPassportNumber.trim() || !blacklistReason.trim()) {
      toast.error('Please provide both passport number and reason');
      return;
    }

    setIsLoading(true);
    try {
      const adminId = localStorage.getItem('userId') || 'admin';
      const result = await verificationService.blacklistPassport(
        blacklistPassportNumber,
        blacklistReason,
        blacklistRequest.userId,
        adminId
      );

      if (result.success) {
        toast.success(result.message || 'Passport blacklisted successfully');
        setBlacklistDialogOpen(false);
        setBlacklistRequest(null);
        setBlacklistPassportNumber('');
        setBlacklistReason('');
        await loadVerificationRequests();
      } else {
        toast.error(result.error || 'Failed to blacklist passport');
      }
    } catch (error) {
      console.error('Error blacklisting passport:', error);
      toast.error('Failed to blacklist passport');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    const colors = {
      pending_review: 'warning',
      pending_manual_review: 'error',
      verified: 'success',
      rejected: 'error'
    } as const;
    return colors[status as keyof typeof colors] || 'default';
  };

  const getStatusIcon = (status: string) => {
    const icons = {
      pending_review: <Schedule />,
      pending_manual_review: <RemoveRedEye />,
      verified: <CheckCircle />,
      rejected: <Cancel />
    };
    return icons[status as keyof typeof icons] || <Warning />;
  };

  const getStatusLabel = (status: string) => {
    if (status === 'pending_manual_review') {
      return 'MANUAL REVIEW';
    }
    return status.replace(/_/g, ' ').toUpperCase();
  };

  const getDocumentTypeIcon = (docType: string) => {
    const icons = {
      passport: <Description />,
      national_id: <Person />,
      driver_license: <VerifiedUser />,
      selfie: <Camera />
    };
    return icons[docType as keyof typeof icons] || <Description />;
  };

  const filteredRequests = (requests || []).filter(request => {
    if (filters.documentType !== 'all' && request.documentType !== filters.documentType) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    loadVerificationRequests();
  }, [filters, pagination.page, pagination.limit]);

  return (
    <Box>
      {/* Header with controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" component="div">
          Verification Requests
          <Chip 
            label={`${pagination.total} total`} 
            size="small" 
            sx={{ ml: 1 }}
          />
        </Typography>
        
        <Stack direction="row" spacing={2}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as any }))}
              displayEmpty
            >
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="pending_manual_review">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RemoveRedEye fontSize="small" color="error" />
                  Manual Review
                </Box>
              </MenuItem>
              <MenuItem value="pending_review">Pending Review</MenuItem>
              <MenuItem value="verified">Verified</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            size="small"
            variant={useEnhancedViewer ? "contained" : "outlined"}
            onClick={() => setUseEnhancedViewer(!useEnhancedViewer)}
            startIcon={<Description />}
          >
            {useEnhancedViewer ? 'Enhanced View' : 'Standard View'}
          </Button>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={filters.documentType}
              onChange={(e) => setFilters(prev => ({ ...prev, documentType: e.target.value as any }))}
              displayEmpty
            >
              <MenuItem value="all">All Documents</MenuItem>
              <MenuItem value="passport">Passport</MenuItem>
              <MenuItem value="national_id">National ID</MenuItem>
              <MenuItem value="driver_license">Driver License</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            startIcon={<Refresh />}
            onClick={loadVerificationRequests}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Stack>
      </Box>

      {/* Loading indicator */}
      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Results table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Document Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.map((request) => (
                <TableRow key={request.verificationId}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                        {request.userInfo.firstName.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {request.userInfo.firstName} {request.userInfo.lastName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {request.userInfo.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {getDocumentTypeIcon(request.documentType)}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        {request.documentType.replace('_', ' ').toUpperCase()}
                      </Typography>
                    </Box>
                  </TableCell>
                  
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(request.status)}
                      label={getStatusLabel(request.status)}
                      color={getStatusColor(request.status)}
                      size="small"
                      sx={request.status === 'pending_manual_review' ? {
                        fontWeight: 600,
                        animation: 'pulse 2s infinite'
                      } : {}}
                    />
                    {request.status === 'pending_manual_review' && request.verificationResults && (
                      <Typography variant="caption" display="block" color="error" sx={{ mt: 0.5 }}>
                        Liveness failed {request.verificationResults.livenessAttempts || 3}x
                      </Typography>
                    )}
                  </TableCell>
                  
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(request.uploadedAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(request.uploadedAt).toLocaleTimeString()}
                    </Typography>
                  </TableCell>
                  
                  <TableCell>
                    <Badge badgeContent={2} color="primary">
                      <Camera fontSize="small" />
                    </Badge>
                  </TableCell>
                  
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Documents">
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewDocuments(request)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      
                      {(request.status === 'pending_review' || request.status === 'pending_manual_review') && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleAction(request, 'approve')}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleAction(request, 'reject')}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>

                          {request.documentType === 'passport' && (
                            <Tooltip title="Blacklist Passport">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleBlacklistPassport(request)}
                              >
                                <Block />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Document Viewer Modal */}
      {selectedRequest && (
        useEnhancedViewer ? (
          <EnhancedVerificationViewer
            open={documentViewerOpen}
            onClose={() => {
              setDocumentViewerOpen(false);
              setSelectedRequest(null);
            }}
            verificationRequest={selectedRequest}
            onApprove={async (verificationId, notes) => {
              setDocumentViewerOpen(false);
              await handleAction(selectedRequest, 'approve');
            }}
            onReject={async (verificationId, reason, notes) => {
              setDocumentViewerOpen(false);
              await handleAction(selectedRequest, 'reject');
            }}
            onFlag={async (verificationId, reason) => {
              setDocumentViewerOpen(false);
            }}
          />
        ) : (
          <VerificationDocumentViewer
            open={documentViewerOpen}
            onClose={() => {
              setDocumentViewerOpen(false);
              setSelectedRequest(null);
            }}
            verificationRequest={selectedRequest}
            onApprove={(verificationId, notes) => {
              setDocumentViewerOpen(false);
              handleAction(selectedRequest, 'approve');
            }}
            onReject={(verificationId, notes) => {
              setDocumentViewerOpen(false);
              handleAction(selectedRequest, 'reject');
            }}
          />
        )
      )}

      {/* Action Confirmation Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)}>
        <DialogTitle>
          Confirm {actionType === 'approve' ? 'Approval' : 'Rejection'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to {actionType} this verification request for{' '}
            <strong>{selectedRequest?.userInfo.firstName} {selectedRequest?.userInfo.lastName}</strong>?
          </Typography>
          
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Admin Notes (Optional)"
            value={actionNotes}
            onChange={(e) => setActionNotes(e.target.value)}
            margin="normal"
            placeholder={`Add notes about this ${actionType} decision...`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleActionConfirm}
            variant="contained"
            color={actionType === 'approve' ? 'success' : 'error'}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : `${actionType === 'approve' ? 'Approve' : 'Reject'}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blacklist Passport Dialog */}
      <Dialog open={blacklistDialogOpen} onClose={() => setBlacklistDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Block />
            Blacklist Passport
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <strong>Warning:</strong> This is a permanent action. The passport number will be blacklisted and cannot be used for future verifications.
          </Alert>
          {blacklistRequest && (
            <Typography variant="body2" gutterBottom>
              User: <strong>{blacklistRequest.userInfo.firstName} {blacklistRequest.userInfo.lastName}</strong>
            </Typography>
          )}
          <TextField
            fullWidth
            label="Passport Number"
            value={blacklistPassportNumber}
            onChange={(e) => setBlacklistPassportNumber(e.target.value.toUpperCase())}
            sx={{ mt: 2, mb: 2 }}
            required
            placeholder="Enter passport number to blacklist"
          />
          <TextField
            fullWidth
            label="Reason for Blacklist"
            multiline
            rows={4}
            value={blacklistReason}
            onChange={(e) => setBlacklistReason(e.target.value)}
            required
            placeholder="Provide detailed reason for blacklisting this passport..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setBlacklistDialogOpen(false);
            setBlacklistRequest(null);
            setBlacklistPassportNumber('');
            setBlacklistReason('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBlacklistConfirm}
            disabled={!blacklistPassportNumber.trim() || !blacklistReason.trim() || isLoading}
            startIcon={<Block />}
          >
            {isLoading ? 'Processing...' : 'Blacklist Passport'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
        severity={snackbar.type}
      />
    </Box>
  );
};

export default AdminVerificationRequestsList;